import React, { useEffect, useState, Suspense } from "react";
import { Routes, Route, Navigate, useLocation, useParams } from "react-router-dom";
import { useAuthStore, useAuthBootstrap, hasRole } from "@/lib/auth";
import { usePresenting } from "@/lib/presenting";
import { supabase } from "@/lib/supabase";
import { startOfflineSync } from "@/lib/offline";
import { AppShell } from "@/components/layout/AppShell";
import OfflineQueueBadge from "@/components/layout/OfflineQueueBadge";   // 10 Oct 2026 — what is still waiting on this phone
import { Spinner } from "@/components/ui";

import LoginScreen          from "@/screens/LoginScreen";
import ResetPasswordScreen from "@/screens/ResetPasswordScreen";
import HomeScreen         from "@/features/home/HomeScreen";
import RootHome           from "@/features/home/RootHome";
// HubScreen (the generic tile grid) is no longer routed — every hub has its own screen; HSE got HseHubScreen on 6 Oct 2026.
import PortalHome         from "@/features/home/PortalHome";
import ProjectHubScreen   from "@/features/home/ProjectHubScreen";
import FinanceHubScreen   from "@/features/home/FinanceHubScreen";
import ManHoursReportScreen from "@/features/reports/ManHoursReportScreen";
import ReportsHubScreen from "@/features/reports/ReportsHubScreen";
import DataExplorerScreen from "@/features/reports/DataExplorerScreen";
import DirectorHubScreen from "@/features/director/DirectorHubScreen";
import ApprovalsScreen from "@/features/approvals/ApprovalsScreen";
import ClientPortalsScreen from "@/features/hop/ClientPortalsScreen";
import IncidentRegisterScreen from "@/features/hse/IncidentRegisterScreen";
import DocumentLibraryScreen from "@/features/hse/DocumentLibraryScreen";
import StartCardScreen from "@/features/hse/StartCardScreen";
import SwmsBuilderScreen from "@/features/hse/SwmsBuilderScreen";
import CraneLibraryScreen from "@/features/hse/CraneLibraryScreen";
import LiftPlanScreen from "@/features/hse/LiftPlanScreen";
import MopBuilderScreen from "@/features/hse/MopBuilderScreen";
import HoursReportsListScreen from "@/features/reports/HoursReportsListScreen";
import HoursReportBuilderScreen from "@/features/reports/HoursReportBuilderScreen";
import HrHubScreen        from "@/features/home/HrHubScreen";
import MyLeaveScreen        from "@/features/leave/MyLeaveScreen";
import LeaveApprovalsScreen from "@/features/leave/LeaveApprovalsScreen";
import InductionsScreen     from "@/features/licences/InductionsScreen";
import HrInductionsScreen   from "@/features/licences/HrInductionsScreen";
import MobileReceiptScreen    from "@/features/receipts/MobileReceiptScreen";
import MyReceiptsScreen       from "@/features/receipts/MyReceiptsScreen";
import PendingReceiptsScreen  from "@/features/receipts/PendingReceiptsScreen";
import MobileTimesheetEntryScreen from "@/features/timesheets/MobileTimesheetEntryScreen";
import TimesheetApprovalsScreen   from "@/features/timesheets/TimesheetApprovalsScreen";
import TimesheetsScreen   from "@/screens/TimesheetsScreen";
import ComplianceScreen   from "@/screens/ComplianceScreen";
import CompanyComplianceScreen from "@/features/compliance/CompanyComplianceScreen";
import { ApCaptureScreen, ApQueueScreen } from "@/features/ap/ApScreens";
import ApHomeScreen from "@/features/ap/ApHomeScreen";
import ApReceiptsCardsScreen from "@/features/ap/ApReceiptsCardsScreen";
import ApXeroScreen from "@/features/ap/ApXeroScreen";
import CardRegisterScreen from "@/features/ap/CardRegisterScreen";
import BillsToPayScreen from "@/features/ap/BillsToPayScreen";
import ReimbursementsScreen from "@/features/ap/ReimbursementsScreen";
import ReceiptsRegisterScreen from "@/features/ap/ReceiptsRegisterScreen";
import ApIntakeScreen          from "@/features/receipts/ApIntakeScreen";
import XeroMatchScreen         from "@/features/ap/XeroMatchScreen";
import { BankReconcileScreen } from "@/features/ap/BankReconcileScreen";
import PlaceholderScreen  from "@/screens/PlaceholderScreen";
import FormsScreen        from "@/screens/FormsScreen";
import MyPoliciesScreen      from "@/features/policies/MyPoliciesScreen";
import HrPoliciesAdminScreen from "@/features/policies/HrPoliciesAdminScreen";
import PolicyReaderScreen     from "@/features/policies/PolicyReaderScreen";

import FormRunner            from "@/features/forms/FormRunner";
import JseaSignonScreen      from "@/features/forms/JseaSignonScreen";
import IncidentReportScreen  from "@/features/forms/IncidentReportScreen";
import VehicleAccidentReportScreen from "@/features/forms/VehicleAccidentReportScreen";

import PermitsScreen                         from "@/features/permits/PermitsScreen";
import { PermitIssueScreen, PermitDetailScreen } from "@/features/permits/PermitIssueScreen";

import { JmpListScreen, JmpNewScreen, JmpDetailScreen } from "@/features/jmp/JmpScreens";

import AcceptancePacksScreen  from "@/features/acceptance/PacksListScreen";
import PackDetailScreen       from "@/features/acceptance/PackDetailScreen";
import ClientAcceptanceScreen from "@/features/acceptance/ClientAcceptanceScreen";

// Phase 4
import ProjectsListScreen       from "@/features/projects/ProjectsListScreen";
import ProjectDetailScreen      from "@/features/projects/ProjectDetailScreen";
import FieldProjectsListScreen  from "@/features/projects/FieldProjectsListScreen";
import FieldProjectDetailScreen from "@/features/projects/FieldProjectDetailScreen";
import NewProjectScreen         from "@/features/projects/NewProjectScreen";
import HopPacksListScreen           from "@/features/hop/HopPacksListScreen";
import HopNewPackScreen             from "@/features/hop/HopNewPackScreen";
import HopPackScreen                from "@/features/hop/HopPackScreen";
import HopCategoryScreen            from "@/features/hop/HopCategoryScreen";
import HopCentreScreen              from "@/features/hop/HopCentreScreen";
import HopCentrePackDetailScreen    from "@/features/hop/HopCentrePackDetailScreen";
import CaptureChooserScreen         from "@/features/capture/CaptureChooserScreen";
import NewDiaryEntryScreen          from "@/features/diary/NewDiaryEntryScreen";
import TakeDeliveryScreen           from "@/features/materials/TakeDeliveryScreen";
import DeliveriesRegisterScreen     from "@/features/materials/DeliveriesRegisterScreen";
import BomRegisterScreen            from "@/features/materials/BomRegisterScreen";   // mig 501 — the Site BOM register
import DefectCentreScreen           from "@/features/defects/DefectCentreScreen";
import ProjectDeliverablesScreen    from "@/features/deliverables/ProjectDeliverablesScreen";
import ProjectScheduleScreen        from "@/features/schedule/ProjectScheduleScreen";
import CrewCalendarScreen           from "@/features/schedule/CrewCalendarScreen";
import DispatchScreen               from "@/features/schedule/DispatchScreen";   // 3 Nov 2026 — the schedule rebuilt: sites × days, people as chips (mig 497)

import MyHorizonScreen              from "@/features/schedule/MyHorizonScreen";
import DisplayBoardScreen           from "@/features/display/DisplayBoardScreen";
import BrainScreen                  from "@/features/admin/BrainScreen";
import DisplayBoardEditScreen       from "@/features/display/DisplayBoardEditScreen";
import AssetRegisterScreen          from "@/features/ap/AssetRegisterScreen";
import CrewAdminScreen              from "@/features/crews/CrewAdminScreen";
import MopsLibraryScreen            from "@/features/mops/MopsLibraryScreen";
import FieldMopsScreen              from "@/features/mops/FieldMopsScreen";
// SDS register on the phone + "add with the camera" (mig 407, 30 Sep 2026)
// HSE alerts (mig 413, 3 Oct 2026) — manager publishes, crew reads + acknowledges
const HseAlertsScreen   = React.lazy(() => import("@/features/alerts/HseAlertsScreen"));
const MyAlertsScreen    = React.lazy(() => import("@/features/alerts/MyAlertsScreen"));
const AlertReaderScreen = React.lazy(() => import("@/features/alerts/AlertReaderScreen"));
const AlertGroupsScreen = React.lazy(() => import("@/features/alerts/AlertGroupsScreen"));   // mig 422 — James's lists
// HSE stand-downs + carrier embargoes (mig 425, 8 Oct 2026) — the schedule refuses bookings inside one
const StanddownsScreen  = React.lazy(() => import("@/features/hse/StanddownsScreen"));
// Planning board (mig 433, 8 Oct 2026) — a private scheduling sandbox per manager; books nothing
const PlanningBoardScreen = React.lazy(() => import("@/features/schedule/PlanningBoardScreen"));
// HSE hub front page (6 Oct 2026) — replaces the generic HubScreen for /hse
const HseHubScreen      = React.lazy(() => import("@/features/hse/HseHubScreen"));
// Admin hub front page (6 Oct 2026) — /admin lands here, not on the user list
const AdminHubScreen    = React.lazy(() => import("@/features/admin/AdminHubScreen"));
import RouteErrorBoundary from "@/components/RouteErrorBoundary";   // 6 Oct 2026 — a crashed page shows, never blanks
const SdsRegisterScreen = React.lazy(() => import("@/features/sds/SdsRegisterScreen"));
const SdsCaptureScreen  = React.lazy(() => import("@/features/sds/SdsCaptureScreen"));
import AdminUsersScreen             from "@/features/admin/AdminUsersScreen";
// Crew tablets (mig 410, 30 Sep 2026) — view-only kiosk logins tied to a crew
const CrewTabletsScreen = React.lazy(() => import("@/features/admin/CrewTabletsScreen"));
const DocumentShelvesScreen = React.lazy(() => import("@/features/admin/DocumentShelvesScreen"));   // 4 Nov 2026 — mig 509
const RecruitmentScreen = React.lazy(() => import("@/features/recruitment/RecruitmentScreen"));   // 5 Nov 2026 — mig 513
// The public apply wizard lives on the website (website/src/pages/apply.astro), not here — James: "Ops stays hidden". ApplyScreen.tsx is unused.
const KioskHome         = React.lazy(() => import("@/features/kiosk/KioskHome"));
import AdminPermissionsScreen       from "@/features/admin/AdminPermissionsScreen";
import FeedbackAdminScreen          from "@/features/admin/FeedbackAdminScreen";
import AssistantAuditScreen         from "@/features/admin/AssistantAuditScreen";
import KbAdminScreen                from "@/features/admin/KbAdminScreen";
import AdminBrandsScreen            from "@/features/admin/AdminBrandsScreen";
import BudgetCodesScreen            from "@/features/finance/BudgetCodesScreen";   // mig 469 — 28 Oct 2026
import { BidsListScreen, BidDetailScreen } from "@/features/bids/BidsScreens";
import PeopleScreen           from "@/features/people/PeopleScreen";
import NewEmployeeScreen      from "@/features/people/NewEmployeeScreen";
import NewSubbieCompanyScreen from "@/features/people/NewSubbieCompanyScreen";
import EmployeeDetailScreen   from "@/features/people/EmployeeDetailScreen";
import ComplianceMatrixScreen from "@/features/compliance/ComplianceMatrixScreen";
import MyComplianceScreen     from "@/features/compliance/MyComplianceScreen";
import AssetsScreen           from "@/features/assets/AssetsScreen";
import ServicesScreen         from "@/features/assets/ServicesScreen";
import OnboardingScreen       from "@/features/onboarding/OnboardingScreen";
import SubbieOnboardingScreen from "@/features/onboarding/SubbieOnboardingScreen";
import AcceptanceHubScreen    from "@/features/acceptance-hub/AcceptanceHubScreen";
import DiaryViewScreen        from "@/features/diary/DiaryViewScreen";
import HopViewScreen          from "@/features/hop/HopViewScreen";
import NewHopSubmissionScreen from "@/features/acceptance-hub/NewHopSubmissionScreen";
import InspectionRegisterScreen from "@/features/inspections/InspectionRegisterScreen";
import InspectionItemScreen     from "@/features/inspections/InspectionItemScreen";
import NewInspectionScreen      from "@/features/inspections/NewInspectionScreen";
import ProfileScreen          from "@/features/profile/ProfileScreen";
import LicencesScreen          from "@/features/licences/LicencesScreen";
import NewLicenceScreen        from "@/features/licences/NewLicenceScreen";
import BulkLicenceIntakeScreen from "@/features/licences/BulkLicenceIntakeScreen";
import LicenceInspectorScreen  from "@/features/licences/LicenceInspectorScreen";
import HrLicencesScreen        from "@/features/licences/HrLicencesScreen";
import EmployeeComplianceScreen from "@/features/licences/EmployeeComplianceScreen";
import RenewalsScreen from "@/features/licences/RenewalsScreen";
import HrLicenceDetailScreen   from "@/features/licences/HrLicenceDetailScreen";
// T-313 / T-252 — VOC module (Phase A)
import EmployeeVocsScreen      from "@/features/licences/EmployeeVocsScreen";
import VocAssessmentScreen     from "@/features/licences/VocAssessmentScreen";
// T-311 — Team week visibility (manager+ read-only timesheet grid)
import TeamWeekScreen          from "@/features/timesheets/TeamWeekScreen";
import PayrollSummaryScreen     from "@/features/payroll/PayrollSummaryScreen";
import EquipmentScreen         from "@/features/equipment/EquipmentScreen";
import HrEquipmentScreen       from "@/features/equipment/HrEquipmentScreen";   // mig 444 — HR → Equipment (manager/supervisor)
import PmoHomeScreen           from "@/features/pmo/PmoHomeScreen";             // mig 446 — PMO client governance
import PmoClientScreen         from "@/features/pmo/PmoClientScreen";
import PmoScheduleScreen       from "@/features/pmo/PmoScheduleScreen";        // mig 447 — client schedule sandbox
import PmoPortfolioScreen      from "@/features/pmo/PmoPortfolioScreen";       // mig 448 — director portfolio
import VehicleDetailScreen     from "@/features/assets/VehicleDetailScreen";
import RiggingDetailScreen     from "@/features/assets/RiggingDetailScreen";
import PowerToolDetailScreen   from "@/features/assets/PowerToolDetailScreen";
import PowerToolNewScreen      from "@/features/assets/PowerToolNewScreen";
import HandToolDetailScreen    from "@/features/assets/HandToolDetailScreen";
import HandToolNewScreen       from "@/features/assets/HandToolNewScreen";
import RiggingNewScreen        from "@/features/assets/RiggingNewScreen";
import SitesScreen             from "@/features/sites/SitesScreen";
import NewSiteScreen           from "@/features/sites/NewSiteScreen";
import SiteDetailScreen        from "@/features/sites/SiteDetailScreen";

// Phase 5 — commercial
import { RateCardsListScreen, RateCardDetailScreen } from "@/features/rates/RateCardsScreens";
import NewRateCardScreen                                  from "@/features/rates/NewRateCardScreen";
import RateCardImportScreen                                from "@/features/rates/RateCardImportScreen";
import OpexScreen                           from "@/features/opex/OpexScreen";
import { QuotesListScreen, NewQuoteScreen } from "@/features/quotes/QuotesListScreen";
// T-130 — QuoteBuilderScreen pulls in pdfmake (~1.4 MB) for PDF generation.
// Lazy-load it so users who don't open a quote don't pay that cost upfront.
// The Suspense wrapper around <Routes> below catches the loading state.
const QuoteBuilderScreen = React.lazy(() => import("@/features/quotes/QuoteBuilderScreen"));
// 26 Sep 2026 — site maps carry Leaflet (~150 KB). Lazy, same rule.
const SiteMapsScreen = React.lazy(() => import("@/features/sitemaps/SiteMapsScreen"));
// 26 Sep 2026 — the crew's Site folder on a job (mig 392). Light, but lazy so
// the shell stays lean.
const SiteFolderScreen = React.lazy(() => import("@/features/sitefolder/SiteFolderScreen"));
import { SuppliersListScreen, SupplierDetailScreen } from "@/features/suppliers/SuppliersScreens";
import { SubbieHubListScreen, SubbieCompanyHubScreen } from "@/features/suppliers/SubbieHubScreens";
import SubbieWorkerBulkTicketsScreen from "@/features/suppliers/SubbieWorkerBulkTicketsScreen";
import MyActionsScreen from "@/features/home/MyActionsScreen";
import { ClientsListScreen, ClientDetailScreen } from '@/features/clients/ClientsScreens';
import { PosListScreen, NewPoScreen, PoDetailScreen } from "@/features/pos/PosScreens";
import { InvoicesListScreen, InvoiceWorksheetScreen } from "@/features/invoices/InvoicesScreens";
import CommercialDashboardScreen            from "@/features/commercial/CommercialDashboardScreen";
import PremisesScreen                       from "@/features/commercial/PremisesScreen";
import VehiclesScreen                       from "@/features/commercial/VehiclesScreen";
import LoansScreen                          from "@/features/commercial/LoansScreen";
import InsuranceScreen                      from "@/features/commercial/InsuranceScreen";

// =============================================================================
// Top-level router
// =============================================================================

// mig 444 — the old per-item inspection forms are gone; send bookmarks to the one form.
function RedirectInspect({ kind, param }: { kind: "rigging" | "power_tool"; param: string }) {
  const params = useParams();
  const id = params[param];
  return <Navigate to={id ? `/inspections/new?item=${id}&kind=${kind}` : "/inspections/new"} replace />;
}

export default function App() {
  useAuthBootstrap();

  // T-508 Presenting mode. App MUST subscribe here, not just AppShell.
  // AppShell receives <Routes> as the `children` prop; if only AppShell
  // re-rendered, `children` would be the same element reference and React
  // would skip the subtree entirely — the numbers would not mask until you
  // navigated. Subscribing in App recreates the element tree below it, so
  // every screen re-renders and fmtMoney is called again. Component state
  // (open modals, form input, scroll) survives, because this is a re-render
  // and not a remount.
  usePresenting((s) => s.presenting);

  useEffect(() => {
    const stop = startOfflineSync();
    return stop;
  }, []);

  const { session, user, loading } = useAuthStore();
  const location = useLocation();

  // Catch Supabase password-recovery + invite flows.
  //
  // Supabase email links arrive with auth tokens in the URL hash. We need to
  // intercept BEFORE the onboarding gate (line below) renders, otherwise a
  // brand-new invitee gets bounced to /onboarding and finishes their profile
  // without ever setting a password — once the temporary recovery session
  // expires (~1 hour), they're locked out.
  //
  // The check has to be SYNCHRONOUS at first render. useEffect runs after
  // render and is too late. So:
  //   1. useState initialiser reads the hash synchronously on first mount.
  //   2. useEffect adds a listener for the PASSWORD_RECOVERY event in case
  //      the SDK fires it slightly later.
  // The flag stays true until the user actually finishes setting a password
  // (ResetPasswordScreen reloads the page, which resets the flag).
  const [recoveryFlow, setRecoveryFlow] = useState(() => {
    if (typeof window === "undefined") return false;
    const h = window.location.hash;
    return h.includes("type=recovery") || h.includes("type=invite");
  });

  useEffect(() => {
    const { data: sub } = supabase.auth.onAuthStateChange((event) => {
      if (event === "PASSWORD_RECOVERY") setRecoveryFlow(true);
    });
    return () => sub.subscription.unsubscribe();
  }, []);

  // Reset scroll to top on every route change. React Router doesn't do this
  // natively, so clicking a new nav item while scrolled down would leave the
  // new page below the fold — looks blank until the user scrolls up.
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  if (loading) {
    return (
      <div className="min-h-screen bg-ink flex items-center justify-center">
        <Spinner size={24} />
      </div>
    );
  }

  // /reset-password — handles Supabase invite + password recovery email links.
  // Must be checked BEFORE the login gate AND before the onboarding gate.
  //   - Login gate: recovery session may not have a public.users row.
  //   - Onboarding gate: a brand-new invitee would get bounced to /onboarding
  //     and finish their profile without setting a password — locked out
  //     once the recovery session expires.
  // Render the screen if either: the user is explicitly on /reset-password,
  // OR we detected a recovery/invite hash on initial mount, OR the SDK fired
  // a PASSWORD_RECOVERY event live.
  if (location.pathname === "/reset-password" || recoveryFlow) {
    return <ResetPasswordScreen />;
  }

  // T-160 — public Acceptance Hub. No auth required; magic-token in URL.
  // Must fire before the login gate so external clients can land here.
  if (location.pathname.startsWith("/accept/")) {
    return (
      <Routes>
        <Route path="/accept/:token" element={<AcceptanceHubScreen />} />
      </Routes>
    );
  }

  // T-326 — public site diary client portal. No auth; unguessable token in URL.
  if (location.pathname.startsWith("/diary-view/")) {
    return (
      <Routes>
        <Route path="/diary-view/:token" element={<DiaryViewScreen />} />
      </Routes>
    );
  }

  // HOP Slice 3 — public HOP client portal. No auth; unguessable token in URL.
  if (location.pathname.startsWith("/hop-view/")) {
    return (
      <Routes>
        <Route path="/hop-view/:token" element={<HopViewScreen />} />
      </Routes>
    );
  }

  if (!session || !user) return <LoginScreen />;

  // Onboarding routes — rendered outside AppShell so the wizard has its own layout.
  // Must appear before the onboarding gate below to avoid infinite redirect.
  // T-154 — /onboard/subbie is the subbie self-service wizard for users
  // invited with login_type='subcontractor'.
  // The Live Board (mig 349) is a full-screen TV surface — its own dark look,
  // no sidebar/topbar. Rendered outside AppShell like onboarding.
  if (location.pathname === "/display") {
    return (
      <Routes>
        <Route path="/display" element={<DisplayBoardScreen />} />
      </Routes>
    );
  }

  // The Coretel Brain (Sep 2026) — full-screen knowledge graph opened in its
  // own tab from the Admin nav. Rendered outside AppShell like the Live
  // Board; sits AFTER the login gate (authed), and the screen itself gates
  // to manager+.
  if (location.pathname === "/brain") {
    return (
      <Routes>
        <Route path="/brain" element={<BrainScreen />} />
      </Routes>
    );
  }

  const isOnboardingRoute =
    location.pathname === "/onboarding" ||
    location.pathname.startsWith("/onboarding/for/") ||
    location.pathname === "/onboard/subbie";

  if (isOnboardingRoute) {
    return (
      <Routes>
        <Route path="/onboarding" element={<OnboardingScreen />} />
        <Route path="/onboarding/for/:targetUserId" element={<OnboardingScreen />} />
        <Route path="/onboard/subbie" element={<SubbieOnboardingScreen />} />
      </Routes>
    );
  }

  // Gate: redirect un-onboarded users to the right wizard.
  // Subbies (login_type='subcontractor') go to /onboard/subbie; everyone
  // else goes to /onboarding.
  if (user.onboarding_completed_at === null) {
    if (user.login_type === "subcontractor") {
      return <Navigate to="/onboard/subbie" replace />;
    }
    return <Navigate to="/onboarding" replace />;
  }

  // T-176 gated HSE to supervisor+ "until the T-180 rebuild lands". James,
  // 26 Sep 2026: "nobody is a supervisor and they won't be ... anyone should
  // be able to complete HSE paperwork apart from a working at heights permit
  // — that needs to be a rigger. A director can sign off on it." So every
  // staff role sees HSE; the screens keep their own approve/lock gates
  // (director approves SWMS, lift plans, MOPs; manager issues permits, locks
  // maps). Supplier subbies (role=subcontractor) still get none of it.
  const canSeeHse = user.role !== "subcontractor";

  return (
    <AppShell user={user}>
      <OfflineQueueBadge />
      <RouteErrorBoundary resetKey={location.pathname}>
      <Suspense fallback={
        <div className="min-h-[40vh] flex items-center justify-center">
          <Spinner size={20}/>
        </div>
      }>
      <Routes>
        {/* Hub landing + per-hub home screens (T-056 Part B — final shape) */}
        <Route path="/"             element={<RootHome />} />
        <Route path="/portal"       element={<PortalHome />} />
        <Route path="/project"      element={<ProjectHubScreen />} />
        <Route path="/finance"      element={<FinanceHubScreen />} />
        <Route path="/hr"           element={<HrHubScreen />} />

        {/* Projects + Bids — field roles get the scrubbed "My jobs" view
            (no financials, team-filtered). Manager+ get the full surface.
            Subcontractor (Supplier subbie, T-154) has no project visibility
            at all — bounce to /profile. */}
        <Route path="/projects" element={
          user.role === "subcontractor"
            ? <Navigate to="/profile" replace />
            : user.role === "kiosk"
              ? <KioskHome />
            : user.role === "field"
              ? <FieldProjectsListScreen />
              : <ProjectsListScreen />
        } />
        <Route path="/projects/new"         element={<NewProjectScreen />} />
        <Route path="/projects/:projectId" element={
          user.role === "subcontractor"
            ? <Navigate to="/profile" replace />
            : (user.role === "field" || user.role === "kiosk")   // mig 410 — the tablet gets the crew's job page
              ? <FieldProjectDetailScreen />
              : <ProjectDetailScreen />
        } />
        {/* T-508 Phase 4 — the AR invoice worksheet is a /projects/* path, so the
            T-451 hub guard passed it for field and supervisor. Branch it the same
            way /projects/:projectId is branched above. */}
        <Route path="/projects/:projectId/invoice/new" element={
          hasRole(user, "manager")
            ? <InvoiceWorksheetScreen />
            : <Navigate to="/projects" replace />
        } />
        {/* Site folder (mig 392, 26 Sep 2026) — every job, everyone on it:
            the map, nearest hospital, who to call, report an incident; and the
            start card / SWMS / permits when Coretel runs the paperwork. */}
        <Route path="/projects/:projectId/site-folder" element={
          user.role === "subcontractor" ? <Navigate to="/profile" replace /> : <SiteFolderScreen />
        } />
        {/* HOP — same shape for every role; RLS + screen-level guards gate
            create/upload. Field/subbie can browse packs for projects they're on. */}
        <Route path="/projects/:projectId/deliverables"                           element={<ProjectDeliverablesScreen />} />
        <Route path="/projects/:projectId/schedule"                               element={<ProjectScheduleScreen />} />
        {/* Crew calendar — T-125. /schedule = cross-project, coloured by
            crew. /crews = admin CRUD for crews + member rosters. The
            screens themselves gate manager+ via useRequireAuth + hasRole. */}
        <Route path="/schedule"                                                   element={<DispatchScreen />} />
        {/* 3 Nov 2026 — James: "keep calendar view as a separate thing and this (Dispatch) is the planning tool we use."
            The month calendar (with Gantt / Workforce / People behind it) opens on its Calendar tab. */}
        <Route path="/schedule/calendar"                                          element={<CrewCalendarScreen />} />
        <Route path="/schedule/classic"                                           element={<CrewCalendarScreen />} />
        {/* Planning board — private per manager, books nothing (mig 433) */}
        <Route path="/schedule/planning" element={<PlanningBoardScreen />} />
        {/* T-245's My week is folded into My schedule (James, 3 Sep: "both a
            bit of a double up") — old links land on the one surface. */}
        <Route path="/my-week"                                                    element={<Navigate to="/my-schedule" replace />} />
        {/* "My schedule" — forward-looking personal schedule (Horizon timeline +
            calendar views), every role. Lives in the Personal Portal. 3 Sep. */}
        <Route path="/my-schedule"                                                element={<MyHorizonScreen />} />
        {/* Live Board editor (mig 349) — managers arrange + publish the TV board. */}
        <Route path="/display/edit"                                               element={<DisplayBoardEditScreen />} />
        {/* Insurance register — mig 351, fed from AP CapEx coding. Lives UNDER
            /assets: the existing Assets screen (vehicles/tools/rigging) keeps
            /assets itself — James, 4 Sep: "there are more assets than just
            large CapEx insured ones". */}
        <Route path="/assets/insurance-register"                                  element={<AssetRegisterScreen />} />
        <Route path="/crews"                                                      element={<CrewAdminScreen />} />
        <Route path="/projects/:projectId/hop"                                    element={<HopPacksListScreen />} />
        <Route path="/projects/:projectId/hop/new"                                element={<HopNewPackScreen />} />
        <Route path="/projects/:projectId/hop/submit/new"                         element={<NewHopSubmissionScreen />} />
        <Route path="/projects/:projectId/hop/:packId"                            element={<HopPackScreen />} />
        <Route path="/projects/:projectId/hop/:packId/categories/:packCategoryId" element={<HopCategoryScreen />} />

        {/* Capture chooser — T-096. Mobile bottom-nav Camera button lands here.
            Single buffer screen between "tap Camera" and the actual capture
            surfaces (HOP / Receipt / Diary). Keeps users in control. */}
        <Route path="/capture"                                                    element={<CaptureChooserScreen />} />
        <Route path="/capture/diary"                                              element={<NewDiaryEntryScreen />} />
        {/* Materials — mig 382. Snap the docket, Ops reads it, confirm the job.
            Any signed-in role can take a delivery; the register is manager+. */}
        <Route path="/capture/delivery"                                           element={<TakeDeliveryScreen />} />
        <Route path="/materials"                                                  element={<DeliveriesRegisterScreen />} />
        <Route path="/materials/bom"                                              element={<BomRegisterScreen />} />

        {/* Acceptance Centre — cross-project HOP review + defect tracking (T-010 C.3c) */}
        <Route path="/acceptance"                                                 element={<HopCentreScreen />} />
        <Route path="/acceptance/defects"                                         element={<DefectCentreScreen />} />
        <Route path="/acceptance/:packId"                                         element={<HopCentrePackDetailScreen />} />
        <Route path="/bids"                 element={<BidsListScreen />} />
        <Route path="/bids/:bidId"          element={<BidDetailScreen />} />

        {/* MOPs Library — T-076. Carrier MOPs (NBN/Optus/Telstra), supplier
            reference docs (LeBlanc tower drawings, RFS data sheets), and
            Coretel internal procedures. Manager+ adds + re-versions. */}
        <Route path="/mops"                 element={
          user.role === "subcontractor" ? <Navigate to="/profile" replace />
            : (user.role === "field" || user.role === "kiosk") ? <FieldMopsScreen />
            : <MopsLibraryScreen />
        } />

        {/* SDS register (mig 407, 30 Sep 2026) — anyone on staff reads the
            sheets and adds a product with the camera; the row lands as
            pending and a manager approves it in HSE → Documents. */}
        {/* HSE alerts — every staff login and the crew tablet read; acknowledging is the person's own act (mig 413) */}
        <Route path="/alerts"          element={user.role === "subcontractor" ? <Navigate to="/profile" replace /> : <MyAlertsScreen />} />
        <Route path="/alerts/:alertId" element={user.role === "subcontractor" ? <Navigate to="/profile" replace /> : <AlertReaderScreen />} />
        <Route path="/sds"     element={user.role === "subcontractor" ? <Navigate to="/profile" replace /> : <SdsRegisterScreen />} />
        <Route path="/sds/add" element={user.role === "subcontractor" ? <Navigate to="/profile" replace /> : user.role === "kiosk" ? <Navigate to="/sds" replace /> : <SdsCaptureScreen />} />

        {/* Admin — T-070 IT Portal. Phase A: director-only user admin
            (list + role edit + deactivate). Phase B+ adds invite + tickets. */}
        <Route path="/admin"                element={<AdminHubScreen />} />
        <Route path="/admin/users"          element={<AdminUsersScreen />} />
        <Route path="/admin/crew-tablets"   element={<CrewTabletsScreen />} />
        <Route path="/admin/document-shelves" element={<DocumentShelvesScreen />} />
        {/* T-129 — director-only role × permission matrix editor.
            Editor only — no enforcement is wired yet (that's T-149). */}
        <Route path="/admin/permissions"    element={<AdminPermissionsScreen />} />
        <Route path="/admin/feedback"       element={<FeedbackAdminScreen />} />
        <Route path="/admin/assistant"      element={<AssistantAuditScreen />} />
        <Route path="/admin/kb"             element={<KbAdminScreen />} />

        {/* Commercial — Phase 5 */}
        <Route path="/commercial"                 element={<CommercialDashboardScreen />} />
        {/* mig 385 — premises cost breakdown + next-12-months OPEX forecast */}
        <Route path="/commercial/premises"        element={<PremisesScreen />} />
        {/* mig 397 — the fleet's running costs by Xero account and by vehicle */}
        <Route path="/commercial/vehicles"        element={<VehiclesScreen />} />
        <Route path="/commercial/loans"           element={<LoansScreen />} />
        <Route path="/commercial/insurance"       element={<InsuranceScreen />} />
        <Route path="/rates/new"           element={<NewRateCardScreen />} />
        <Route path="/rates/import-pdf"    element={<RateCardImportScreen />} />
        <Route path="/rates"                      element={<RateCardsListScreen />} />
        <Route path="/rates/:rateCardId"          element={<RateCardDetailScreen />} />
        {/* T-388 — company-level compliance hub (insurances first; screen self-gates manager+) */}
        <Route path="/company-compliance"         element={<CompanyComplianceScreen />} />
        <Route path="/opex"                       element={<OpexScreen />} />
        {/* T-395 — director hub (screen + RLS both director-only) */}
        <Route path="/director"                   element={<DirectorHubScreen />} />
        {/* T-398 — approvals register (self-gates manager view / director decide) */}
        <Route path="/approvals"                  element={<ApprovalsScreen />} />
        {/* T-417 — company-wide client portal manager (self-gates manager+) */}
        <Route path="/client-portals"             element={<ClientPortalsScreen />} />
        {/* T-392 — reporting hub + generic data explorer (screens self-gate manager+) */}
        <Route path="/reports"                    element={<ReportsHubScreen />} />
        <Route path="/reports/explore/:datasetId" element={<DataExplorerScreen />} />
        <Route path="/reports/man-hours"          element={<ManHoursReportScreen />} />
        <Route path="/reports/hours"              element={<HoursReportsListScreen />} />
        <Route path="/reports/hours/new"          element={<HoursReportBuilderScreen />} />
        <Route path="/reports/hours/:reportId"    element={<HoursReportBuilderScreen />} />
        {/* T-296 — supplier brand-collapse admin (manager+; screen self-gates) */}
        <Route path="/finance/brands"             element={<AdminBrandsScreen />} />
        <Route path="/finance/budget-codes"       element={<BudgetCodesScreen />} />

        {/* Suppliers */}
        <Route path="/suppliers"                  element={<SuppliersListScreen />} />
        {/* T-319 — Subbie Compliance Centre (manager+; screen self-gates).
            Route sits before the dynamic /suppliers/:supplierId so it can't be
            swallowed as a supplier_id value. */}
        {/* T-381 polish — the Compliance Centre is fully absorbed by the Hub
            (same data, same actions, one page). Old URL redirects. */}
        <Route path="/subbies/compliance"         element={<Navigate to="/subbies" replace />} />
        <Route path="/subbies"                    element={<SubbieHubListScreen />} />
        <Route path="/subbies/:companyId"         element={<SubbieCompanyHubScreen />} />
        {/* 15 Sep 2026 — a subbie worker's folder of tickets, read by the same AI as staff licences */}
        <Route path="/subbies/:companyId/workers/:workerId/bulk" element={<SubbieWorkerBulkTicketsScreen />} />
        {/* Single dynamic route — "new" is treated as the supplier_id value.
            The screen has `isNew = supplierId === "new"` and renders the
            create form when true. (T-085 fix: a literal "/suppliers/new"
            route used to win the match but had no :supplierId param, so
            isNew was always false and the screen dead-ended on
            "Supplier not found.") */}
        <Route path="/suppliers/:supplierId"      element={<SupplierDetailScreen />} />
        
        {/* Clients */}
        <Route path="/clients"                    element={<ClientsListScreen />} />
        <Route path="/clients/new"                element={<ClientDetailScreen />} />
        <Route path="/clients/:clientId"          element={<ClientDetailScreen />} />
        
        {/* Quotes */}
        <Route path="/quotes"                     element={<QuotesListScreen />} />
        <Route path="/quotes/new"                 element={<NewQuoteScreen />} />
        <Route path="/quotes/:quoteId"            element={<QuoteBuilderScreen />} />

        {/* Purchase Orders */}
        <Route path="/pos"                        element={<PosListScreen />} />
        <Route path="/pos/new"                    element={<NewPoScreen />} />
        <Route path="/pos/:poId"                  element={<PoDetailScreen />} />

        {/* Invoices (AR) */}
        <Route path="/invoices"                   element={<InvoicesListScreen />} />

        <Route path="/ap"       element={<ApHomeScreen />} />
        <Route path="/ap/queue" element={<ApQueueScreen />} />
        <Route path="/ap/xero"  element={<ApXeroScreen />} />
        <Route path="/ap/cards" element={<CardRegisterScreen />} />
        <Route path="/ap/bills" element={<BillsToPayScreen />} />
        <Route path="/ap/reimbursements" element={<ReimbursementsScreen />} />
        <Route path="/ap/register" element={<ReceiptsRegisterScreen />} />
        <Route path="/ap/new"   element={<ApCaptureScreen />} />
        <Route path="/ap/reconcile" element={<BankReconcileScreen />} />
        {/* T-086 Phase B step 2 — bulk PDF intake with AI extraction */}
        <Route path="/ap/intake" element={<ApIntakeScreen />} />
        {/* T-103 — Xero rec catch-up: drop CSV from Xero, auto-match, mark paid */}
        <Route path="/ap/xero-match" element={<XeroMatchScreen />} />

        {/* HSE — Phase 2. T-176: gated to supervisor+ until the T-180 rebuild
            lands. Field + subbie roles fall through to the catch-all "/" redirect. */}
        {canSeeHse && <>
          <Route path="/hse"                          element={<HseHubScreen />} />
          <Route path="/forms"                        element={<FormsScreen />} />
          <Route path="/forms/run/INCIDENT"           element={<IncidentReportScreen />} />
          <Route path="/forms/run/VEHICLE_ACCIDENT"  element={<VehicleAccidentReportScreen />} />   {/* 1 Nov 2026 — the one-page vehicle accident report */}
          <Route path="/forms/run/:templateCode"      element={<FormRunner />} />
          <Route path="/forms/signon/:submissionId"   element={<JseaSignonScreen />} />
          <Route path="/permits"                      element={<PermitsScreen />} />
          <Route path="/permits/issue/:permitType"    element={<PermitIssueScreen />} />
          <Route path="/permits/:permitId"            element={<PermitDetailScreen />} />
          <Route path="/jmp"                          element={<JmpListScreen />} />
          <Route path="/jmp/new"                      element={<JmpNewScreen />} />
          <Route path="/jmp/:jmpId"                   element={<JmpDetailScreen />} />
          <Route path="/compliance"                   element={<ComplianceScreen />} />
        </>}

        {/* People + assets */}
        <Route path="/assets"                   element={<AssetsScreen />} />
        {/* Mig 357 / T-ASSET-LIFECYCLE item 1: the depot-rotation queue — reads
            v_asset_services_due, records nothing, routes into existing flows. */}
        <Route path="/assets/services"          element={<ServicesScreen />} />
        <Route path="/assets/vehicles/new"      element={<VehicleDetailScreen />} />
        <Route path="/assets/vehicles/:vehicleId" element={<VehicleDetailScreen />} />
        <Route path="/assets/rigging/new"                     element={<RiggingNewScreen />} />
        <Route path="/assets/rigging/:riggingId"              element={<RiggingDetailScreen />} />
        {/* mig 444 — the raw-insert inspection forms are retired; one form at /inspections/new */}
        <Route path="/assets/rigging/:riggingId/inspect"      element={<RedirectInspect kind="rigging" param="riggingId" />} />
        <Route path="/assets/power-tools/new"                 element={<PowerToolNewScreen />} />
        <Route path="/assets/power-tools/:toolId"             element={<PowerToolDetailScreen />} />
        <Route path="/assets/power-tools/:toolId/test-tag"    element={<RedirectInspect kind="power_tool" param="toolId" />} />
        <Route path="/assets/hand-tools/new"                  element={<HandToolNewScreen />} />
        <Route path="/assets/hand-tools/:toolId"              element={<HandToolDetailScreen />} />
        <Route path="/people"               element={<PeopleScreen />} />
        <Route path="/people/new"           element={<NewEmployeeScreen />} />
        <Route path="/people/subbies/new"   element={<NewSubbieCompanyScreen />} />
        {/* T-319 Slice 4 — edit an existing subbie company (create screen reused) */}
        <Route path="/people/subbies/:companyId" element={<NewSubbieCompanyScreen />} />
        <Route path="/people/:employeeId"   element={<EmployeeDetailScreen />} />
        <Route path="/hr/compliance/matrix" element={<ComplianceMatrixScreen />} />
        {/* mig 444 — rigging, power tools, ladders: track, allocate, approve, export */}
        <Route path="/hr/equipment"         element={<HrEquipmentScreen />} />
        {/* mig 446 — PMO: the client governance dashboard James presents in client meetings (manager+) */}
        <Route path="/pmo"                  element={<PmoHomeScreen />} />
        <Route path="/pmo/portfolio"        element={<PmoPortfolioScreen />} />
        <Route path="/pmo/:clientId"        element={<PmoClientScreen />} />
        <Route path="/pmo/:clientId/schedule" element={<PmoScheduleScreen />} />
        <Route path="/my-compliance"        element={<MyComplianceScreen />} />

        {/* T-155 Inspection registers — rigging colour-tag + power tool test/tag.
            Subcontractor role bounced (no asset visibility). 12 Oct 2026: ONE
            register for every employee — field, supervisor, manager, director
            all see the full audit view (James: a client can shut a site down if
            a crew member can't show the record on a block he isn't allocated). */}
        <Route path="/inspections" element={
          user.role === "subcontractor"
            ? <Navigate to="/profile" replace />
            : <InspectionRegisterScreen />
        } />
        {/* 12 Oct 2026 — the create screens mounted under the HSE hub so crews can
            reach them (the /assets copies sit in the HR hub, which field logins
            don't hold). Same components; they read the path to come back here. */}
        <Route path="/inspections/add/rigging" element={
          (user.role === "subcontractor" || user.role === "kiosk")
            ? <Navigate to={user.role === "kiosk" ? "/inspections" : "/profile"} replace />
            : <RiggingNewScreen />
        } />
        <Route path="/inspections/add/power-tool" element={
          (user.role === "subcontractor" || user.role === "kiosk")
            ? <Navigate to={user.role === "kiosk" ? "/inspections" : "/profile"} replace />
            : <PowerToolNewScreen />
        } />
        <Route path="/inspections/new" element={
          (user.role === "subcontractor" || user.role === "kiosk")
            ? <Navigate to={user.role === "kiosk" ? "/inspections" : "/profile"} replace />
            : <NewInspectionScreen />
        } />
        <Route path="/inspections/equipment/:kind/:itemId" element={
          user.role === "subcontractor"
            ? <Navigate to="/profile" replace />
            : <InspectionItemScreen />
        } />

        {/* Time */}
        <Route path="/timesheets"      element={<TimesheetsScreen />} />
        <Route path="/timesheets/new"  element={<MobileTimesheetEntryScreen />} />

        {/* Self-service — T-002 */}
        <Route path="/profile"      element={<ProfileScreen />} />
        {/* T-350 — cross-project "my actions" glance (to-dos + reminders) */}
        <Route path="/my-actions"   element={<MyActionsScreen />} />

        {/* Acceptance packs */}
        <Route path="/packs"                    element={<AcceptancePacksScreen />} />
        <Route path="/packs/:packId"            element={<PackDetailScreen />} />
        <Route path="/packs/:packId/sign-off"   element={<ClientAcceptanceScreen />} />

        {/* === T-036 placeholders — replace each with the real screen as its ticket ships === */}
        {/* Portal (personal scope) */}
        <Route path="/leave"                element={<MyLeaveScreen />} />
        <Route path="/licences"             element={<LicencesScreen />} />
        <Route path="/policies/:policyCode"     element={<PolicyReaderScreen />} />
        <Route path="/policies"     element={<MyPoliciesScreen />} />
        <Route path="/licences/new"         element={<NewLicenceScreen />} />
        {/* 15 Sep 2026 — drop a folder of tickets, AI reads each, review, save all */}
        <Route path="/licences/bulk"        element={<BulkLicenceIntakeScreen />} />
        <Route path="/inductions"           element={<InductionsScreen />} />
        <Route path="/inductions/new"       element={<NewLicenceScreen />} />
        <Route path="/receipts"             element={<MyReceiptsScreen />} />
        <Route path="/receipts/new"         element={<MobileReceiptScreen />} />
        <Route path="/ap/receipts"          element={<ApReceiptsCardsScreen />} />
        <Route path="/licences/inspector"   element={<LicenceInspectorScreen />} />
        <Route path="/equipment"            element={<EquipmentScreen />} />

        {/* Project */}
        <Route path="/sites"            element={<SitesScreen />} />
        <Route path="/sites/new"        element={<NewSiteScreen />} />
        <Route path="/sites/:siteId"    element={<SiteDetailScreen />} />

        {/* HR (director + manager) */}
        <Route path="/hr/leave-approvals"            element={<LeaveApprovalsScreen />} />
        <Route path="/hr/timesheet-approvals"        element={<TimesheetApprovalsScreen />} />
        {/* T-390 — dashboard fronting the licence register */}
        <Route path="/hr/compliance"                 element={<EmployeeComplianceScreen />} />
        <Route path="/hr/renewals"                   element={<RenewalsScreen />} />
        <Route path="/hr/recruitment"                element={<RecruitmentScreen />} />
        <Route path="/hr/licences"                   element={<HrLicencesScreen />} />
        <Route path="/hr/licences/:employeeId"       element={<HrLicenceDetailScreen />} />
        <Route path="/hr/licences/:employeeId/new"   element={<NewLicenceScreen />} />
        <Route path="/hr/licences/:employeeId/bulk"  element={<BulkLicenceIntakeScreen />} />
        {/* T-313 / T-252 — VOC module Phase A */}
        <Route path="/hr/vocs/employee/:employee_id" element={<EmployeeVocsScreen />} />
        <Route path="/hr/vocs/:assessment_id"        element={<VocAssessmentScreen />} />
        {/* T-311 — Team week visibility */}
        <Route path="/hr/team-week"                  element={<TeamWeekScreen />} />
        <Route path="/hr/inductions"      element={<HrInductionsScreen />} />
        <Route path="/hr/policies"        element={<HrPoliciesAdminScreen />} />
        <Route path="/hr/payroll"         element={<Navigate to="/hr/payroll/summary" replace />} />
        <Route path="/hr/payroll/summary" element={<PayrollSummaryScreen />} />

        {/* HSE sub-screens (Dashboard/Permits/JMP/Compliance/Assets already exist).
            T-176: same supervisor+ gate as the rest of HSE. */}
        {canSeeHse && <>
          {/* T-436 — lift plans + the crane chart library they calculate from */}
          <Route path="/hse/cranes"        element={<CraneLibraryScreen />} />
          <Route path="/hse/lift-plans"    element={<LiftPlanScreen />} />
          <Route path="/hse/lift-plans/:liftPlanId" element={<LiftPlanScreen />} />
          {/* T-MOP — generated Methods of Procedure (mig 362), sibling of lift plans */}
          <Route path="/hse/mops"          element={<MopBuilderScreen />} />
          <Route path="/hse/mops/:mopId"   element={<MopBuilderScreen />} />
          {/* Site maps (mig 390, 26 Sep 2026) — the mud map / MOP layout drawn over satellite imagery */}
          <Route path="/hse/site-maps"     element={<SiteMapsScreen />} />
          <Route path="/hse/site-maps/:siteMapId" element={<SiteMapsScreen />} />
          <Route path="/hse/swms"          element={<SwmsBuilderScreen />} />
          <Route path="/hse/swms/site/:projectSwmsId" element={<SwmsBuilderScreen />} />
          <Route path="/hse/swms/:swmsId"  element={<SwmsBuilderScreen />} />
          <Route path="/hse/jsa"         element={<PlaceholderScreen name="JSA templates" description="Per-activity Job Safety Analyses. Issue, sign, track. Often nested within a SWMS instance." ticket="T-015" phase="Phase 5" />} />
          <Route path="/hse/site-folder" element={<PlaceholderScreen name="Digital site folder" description="Per-site view aggregating SWMS, JSA, permits, HOP, diary, plus a touchscreen-drawn mud map for exclusion zones / access paths / spotters / powerlines. One URL per site." ticket="T-016" phase="Phase 5" />} />
          <Route path="/hse/incidents"   element={<IncidentRegisterScreen />} />
          {/* HSE alerts — publish, target, track acknowledgements (mig 413, 3 Oct 2026) */}
          <Route path="/hse/alerts"          element={hasRole(user, "manager") ? <HseAlertsScreen /> : <Navigate to="/alerts" replace />} />
          <Route path="/hse/alerts/new"      element={hasRole(user, "manager") ? <HseAlertsScreen /> : <Navigate to="/alerts" replace />} />
          <Route path="/hse/alerts/groups"   element={hasRole(user, "manager") ? <AlertGroupsScreen /> : <Navigate to="/alerts" replace />} />
          <Route path="/hse/alerts/:alertId" element={hasRole(user, "manager") ? <HseAlertsScreen /> : <Navigate to="/alerts" replace />} />
          {/* Stand-downs & carrier embargoes — every staff login reads; the screen gates raise / clear to manager+ (mig 425, 8 Oct 2026) */}
          <Route path="/hse/standdowns"              element={<StanddownsScreen />} />
          <Route path="/hse/standdowns/new"          element={<StanddownsScreen />} />
          <Route path="/hse/standdowns/:standdownId" element={<StanddownsScreen />} />
          <Route path="/hse/documents"   element={<DocumentLibraryScreen />} />
          <Route path="/hse/start-card"  element={<StartCardScreen />} />
          <Route path="/hse/start-card/:cardId" element={<StartCardScreen />} />
        </>}

        <Route path="*"             element={<Navigate to="/" replace />} />
      </Routes>
      </Suspense>
      </RouteErrorBoundary>
    </AppShell>
  );
}
